import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState, TableSkeleton } from "@/components/common";
import { DirectionBadge, ScoreBadge, LANG_NAMES, fmtDur, fmtTime } from "@/lib/format";
import api from "@/lib/api";

function CriteriaDots({ scores }) {
  const keys = Object.keys(scores || {});
  if (!keys.length) return <span className="text-slate-300 text-xs">—</span>;
  return <div className="flex gap-1">{keys.map((k) => <span key={k} title={`${k}: ${scores[k]} pts`} className={`w-2 h-2 rounded-full ${scores[k] ? "bg-emerald-500" : "bg-slate-200 dark:bg-slate-600"}`} />)}</div>;
}

function OutcomeBadge({ outcome }) {
  const o = (outcome || "").toLowerCase();
  const c = o.includes("qualif") ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
    : o.includes("not") ? "bg-slate-100 text-slate-500 dark:bg-slate-500/15 dark:text-slate-400"
    : (o.includes("no-answer") || o.includes("no answer")) ? "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400"
    : "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400";
  return <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap ${c}`}>{outcome || "—"}</span>;
}

function CallModal({ id, onClose }) {
  const [call, setCall] = useState(null);
  useEffect(() => { api.get(`/calls/${id}`).then((r) => setCall(r.data)); }, [id]);
  if (!call) return <div className="py-10 text-center text-slate-400">Loading...</div>;
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="flex items-center gap-2"><DirectionBadge direction={call.direction} /><ScoreBadge score={call.score} /><span className="text-sm text-slate-500">{call.outcome}</span><span className="ml-auto text-xs text-slate-400">{fmtTime(call.started_at)}</span></div>
      <div className="grid grid-cols-3 gap-3 text-sm">
        {[["Lead", call.lead?.name],["Duration", fmtDur(call.duration_secs)],["Language", LANG_NAMES[call.language_detected]||call.language_detected],["Sentiment", call.sentiment],["AB Variant", call.ab_variant],["City", call.lead?.city]].map(([l,v]) => <div key={l}><div className="text-[11px] text-slate-400">{l}</div><div className="font-medium">{v||"—"}</div></div>)}
      </div>
      <Card className="border shadow-none p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Score Breakdown</div>
        {call.criteria_def.map((c) => <div key={c.key} className="flex justify-between text-sm py-1 border-b last:border-0"><span>{c.label}</span><span className={call.criterion_scores?.[c.key] ? "text-emerald-600 font-medium" : "text-slate-400"}>{call.criterion_scores?.[c.key] || 0} pts</span></div>)}
      </Card>
      <Card className="border shadow-none p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Sentiment Timeline</div>
        <ResponsiveContainer width="100%" height={140}>
          <LineChart data={call.sentiment_timeline}><CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="t" fontSize={10} tickFormatter={(t) => `${t}s`} /><YAxis domain={[-1, 1]} fontSize={10} /><Tooltip /><ReferenceLine y={0} stroke="#94a3b8" /><Line type="monotone" dataKey="value" stroke="#6366F1" strokeWidth={2} dot={false} /></LineChart>
        </ResponsiveContainer>
      </Card>
      <Card className="border shadow-none p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Transcript</div>
        {call.transcript ? (
          <div className="space-y-2">{call.transcript.split("\n").filter(l => l.trim()).map((line, i) => {
            const idx = line.indexOf(":");
            const spk = idx > 0 ? line.slice(0, idx).trim() : "";
            const text = idx > 0 ? line.slice(idx + 1).trim() : line;
            const isAgent = /akriti|assistant|\bai\b|agent|bot|system/i.test(spk);
            const label = isAgent ? "AKRITI" : (spk && !/caller|user|human|customer|lead|client/i.test(spk) ? spk.toUpperCase() : "CALLER");
            return <div key={i} className={`text-sm p-2 rounded-lg ${isAgent ? "bg-indigo-50 dark:bg-indigo-500/10" : "bg-slate-50 dark:bg-slate-800"}`}><span className="font-semibold mr-1">{label}:</span>{text}</div>;
          })}</div>
        ) : <div className="text-sm text-slate-400 py-2">No transcript available</div>}
      </Card>
    </div>
  );
}

export default function CallLogs() {
  const [data, setData] = useState(null);
  const [dir, setDir] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams({ limit: "100" });
    if (dir !== "all") q.set("direction", dir);
    if (outcome !== "all") q.set("outcome", outcome);
    api.get(`/calls?${q}`).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [dir, outcome]);

  const exportCsv = () => {
    if (!data?.items?.length) return;
    const cols = ["started_at","direction","lead_ref","lead_name","city","duration_secs","language_detected","outcome","score","ab_variant"];
    const rows = [cols.join(","), ...data.items.map((c) => cols.map((k) => `"${c[k] ?? ""}"`).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "call_logs.csv"; a.click();
    toast.success("Exported CSV");
  };

  return (
    <div>
      <PageHeader title="Call Logs" subtitle="All inbound & outbound calls">
        <Select value={dir} onValueChange={setDir}><SelectTrigger className="w-36" data-testid="log-direction"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Directions</SelectItem><SelectItem value="outbound">Outbound</SelectItem><SelectItem value="inbound">Inbound</SelectItem></SelectContent></Select>
        <Select value={outcome} onValueChange={setOutcome}><SelectTrigger className="w-36"><SelectValue placeholder="Outcome" /></SelectTrigger><SelectContent><SelectItem value="all">All Outcomes</SelectItem><SelectItem value="Qualified">Qualified</SelectItem><SelectItem value="Demo Booked">Demo Booked</SelectItem><SelectItem value="No Answer">No Answer</SelectItem></SelectContent></Select>
        <Button variant="outline" onClick={exportCsv}><Download className="w-4 h-4 mr-1" />Export</Button>
      </PageHeader>

      {!data ? <Card className="border shadow-none"><TableSkeleton cols={8} /></Card> :
        data.items.length === 0 ? <Card className="border shadow-none"><EmptyState title="No calls logged" description="Calls will appear here after your first campaign runs." /></Card> :
        <Card className="border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b bg-slate-50 dark:bg-slate-800/50"><tr>
                {["Date/Time","Direction","Lead ID","Name","City","Duration","Lang","Outcome","Score","Criteria","Variant"].map((h) => <th key={h} className="text-left px-3 py-3 font-medium whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-accent cursor-pointer" onClick={() => setSel(c.id)} data-testid={`call-row-${c.id}`}>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs">{fmtTime(c.started_at)}</td>
                    <td className="px-3 py-2.5"><DirectionBadge direction={c.direction} /></td>
                    <td className="px-3 py-2.5 font-mono text-xs">{c.lead_ref}</td>
                    <td className="px-3 py-2.5 font-medium">{c.lead_name}</td>
                    <td className="px-3 py-2.5 text-slate-500">{c.city||"—"}</td>
                    <td className="px-3 py-2.5">{fmtDur(c.duration_secs)}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{LANG_NAMES[c.language_detected]||c.language_detected}</td>
                    <td className="px-3 py-2.5"><OutcomeBadge outcome={c.outcome} /></td>
                    <td className="px-3 py-2.5"><ScoreBadge score={c.score} /></td>
                    <td className="px-3 py-2.5"><CriteriaDots scores={c.criterion_scores} /></td>
                    <td className="px-3 py-2.5 text-xs">{c.ab_variant}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      }

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle className="font-display">Call Detail</DialogTitle></DialogHeader>{sel && <CallModal id={sel} onClose={() => setSel(null)} />}</DialogContent>
      </Dialog>
    </div>
  );
}
